import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * The controls a gloved hand can hit.
 *
 * Sizes are not a style choice. In a Laval January the other hand is holding a light, a hose
 * or a ladder: targets are 44 px minimum and a primary action is 56, reachable with a thumb,
 * and **nothing important lives behind a hover or a long-press** — neither exists for somebody
 * wearing gloves.
 */

export function BigButton({
  onClick,
  children,
  tone = 'primary',
  disabled,
  busy,
  hint,
}: {
  onClick: () => void;
  children: ReactNode;
  tone?: 'primary' | 'warm' | 'danger' | 'quiet' | 'positive';
  disabled?: boolean;
  busy?: boolean;
  hint?: string;
}) {
  return (
    <button
      type="button"
      className={`big big--${tone}`}
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
    >
      <span className="big__label">{children}</span>
      {hint && <span className="big__hint">{hint}</span>}
    </button>
  );
}

/**
 * A chip.
 *
 * Access requirements, capture kinds, reasons — everything the business declared shows up as
 * one of these, because a list of words a technician recognises beats a dropdown they have to
 * open with a glove on.
 */
export function Chip({
  children,
  on,
  onToggle,
  tone = 'neutral',
}: {
  children: ReactNode;
  on?: boolean;
  onToggle?: () => void;
  tone?: 'neutral' | 'warm' | 'danger' | 'positive';
}) {
  if (!onToggle) return <span className={`chip chip--${tone}`}>{children}</span>;
  return (
    <button type="button" className={`chip chip--${tone}${on ? ' chip--on' : ''}`} onClick={onToggle} aria-pressed={on}>
      {children}
    </button>
  );
}

/** A row of the stop's checklist: done, or not, and never a percentage. */
export function CheckRow({ done, children, warn }: { done: boolean; children: ReactNode; warn?: boolean }) {
  return (
    <li className={`check${done ? ' check--done' : ''}${warn ? ' check--warn' : ''}`}>
      <span className="check__mark" aria-hidden="true">
        {done ? '✓' : '○'}
      </span>
      <span className="check__label">{children}</span>
    </li>
  );
}

/**
 * A sheet that slides up from the bottom.
 *
 * Everything secondary happens in one of these rather than on a new screen: a technician with
 * one hand free should not have to find their way back.
 */
export function Sheet({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="sheet__scrim" onClick={onClose}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="sheet__head">
          <h2>{title}</h2>
          <button type="button" className="sheet__close" onClick={onClose} aria-label="Fermer">
            ✕
          </button>
        </header>
        <div className="sheet__body">{children}</div>
      </div>
    </div>
  );
}

/**
 * A big numeric keypad.
 *
 * The alternative is the phone's own keyboard, which on a cold morning means three taps to
 * reach a decimal point. Dictation is the other path and is offered beside this one.
 */
export function Keypad({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const press = (key: string) => {
    if (key === '⌫') return onChange(value.slice(0, -1));
    if (key === ',' && value.includes(',')) return;
    onChange(value + key);
  };

  return (
    <div className="keypad" role="group" aria-label="Clavier numérique">
      {['1', '2', '3', '4', '5', '6', '7', '8', '9', ',', '0', '⌫'].map((key) => (
        <button type="button" key={key} className="keypad__key" onClick={() => press(key)}>
          {key}
        </button>
      ))}
    </div>
  );
}

/**
 * Somewhere to sign.
 *
 * A canvas and pointer events, which is all a signature is. Exported as a JPEG blob so it goes
 * through exactly the same capture path as a photograph — a signature is a capture with a
 * purpose, not a separate kind of thing.
 */
export function SignaturePad({ onDone, label }: { onDone: (blob: Blob) => void; label: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.scale(ratio, ratio);
    context.lineWidth = 2.5;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#f2f5f6';
  }, []);

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    canvas.setPointerCapture(event.pointerId);
    const rect = canvas.getBoundingClientRect();
    context.beginPath();
    context.moveTo(event.clientX - rect.left, event.clientY - rect.top);
    setDrawn(true);
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!event.buttons && event.pointerType === 'mouse') return;
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context || !drawn) return;
    const rect = canvas.getBoundingClientRect();
    context.lineTo(event.clientX - rect.left, event.clientY - rect.top);
    context.stroke();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    setDrawn(false);
  };

  const done = () => {
    const canvas = canvasRef.current;
    if (!canvas || !drawn) return;
    // Flattened onto the field surface's own ground so the signature reads on paper as it does
    // on screen, rather than as white strokes on transparency.
    const flat = document.createElement('canvas');
    flat.width = canvas.width;
    flat.height = canvas.height;
    const context = flat.getContext('2d');
    if (!context) return;
    context.fillStyle = '#171c21';
    context.fillRect(0, 0, flat.width, flat.height);
    context.drawImage(canvas, 0, 0);
    flat.toBlob((blob) => blob && onDone(blob), 'image/jpeg', 0.9);
  };

  return (
    <div className="sign">
      <p className="sign__label">{label}</p>
      <canvas
        ref={canvasRef}
        className="sign__pad"
        onPointerDown={start}
        onPointerMove={move}
        aria-label="Zone de signature"
      />
      <div className="sign__actions">
        <button type="button" className="ghost" onClick={clear}>
          Effacer
        </button>
        <BigButton onClick={done} disabled={!drawn}>
          Signer
        </BigButton>
      </div>
    </div>
  );
}

/**
 * The camera.
 *
 * A plain file input with `capture`, not a `getUserMedia` viewfinder. Two reasons and both are
 * about the van: the phone's own camera app focuses and exposes better than anything this
 * could build, and it is the one interface every technician already knows how to use with one
 * hand.
 */
export function CameraButton({
  onFile,
  children,
  tone = 'primary',
  accept = 'image/*',
}: {
  onFile: (file: File) => void;
  children: ReactNode;
  tone?: 'primary' | 'warm' | 'danger' | 'quiet' | 'positive';
  accept?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        capture="environment"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onFile(file);
          event.target.value = '';
        }}
      />
      <BigButton tone={tone} onClick={() => inputRef.current?.click()}>
        {children}
      </BigButton>
    </>
  );
}
